import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getGastosCompras, saveGastoCompra } from '../../../../firebase/facturacionUtils';
import ModalGastoCompra from '../Modales/ModalGastoCompra';
import { formatCurrency, getStatusBadgeStyle, formatDate } from '../helpers';

const DIAS_SEMANA = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MESES = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const IconArrowLeft = () => (
    <svg className="w-5 h-5 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/>
    </svg>
);

const CalendarioPagosTab = ({ maestros = {}, onBack }) => {
    const navigate = useNavigate();
    const [gastos, setGastos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentDate, setCurrentDate] = useState(new Date());

    const todayObj = new Date();
    const todayKey = `${todayObj.getFullYear()}-${String(todayObj.getMonth() + 1).padStart(2, '0')}-${String(todayObj.getDate()).padStart(2, '0')}`;

    const [selectedDateKey, setSelectedDateKey] = useState(todayKey);
    const [showModal, setShowModal] = useState(false);
    const [editingGasto, setEditingGasto] = useState(null);

    const handleVolver = () => {
        if (onBack) {
            onBack();
        } else {
            navigate('/admin/facturacion-gestion');
        }
    };

    const fetchGastos = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getGastosCompras();
            setGastos(data || []);
        } catch (error) {
            console.error("Error al cargar gastos para el calendario:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchGastos();
    }, [fetchGastos]);

    const isGastoVencido = useCallback((gasto) => {
        if (gasto.estatus === 'Pagado') return false;
        const fechaVenc = (gasto.fechaVencimientoGasto || gasto.fechaGasto || '').substring(0, 10);
        return fechaVenc !== '' && fechaVenc < todayKey;
    }, [todayKey]);

    const { mapaEventosPorFecha, totalGastosVencidos } = useMemo(() => {
        const mapa = {};
        let vencidosCount = 0;

        gastos.forEach(gasto => {
            const tienePlan = gasto.esPlanPago && Array.isArray(gasto.cuotasPlan) && gasto.cuotasPlan.length > 0;
            const tieneCheques = Array.isArray(gasto.chequesList) && gasto.chequesList.length > 0;

            if (isGastoVencido(gasto)) {
                vencidosCount++;
            }

            // 1. COMPROBANTE ESTÁNDAR
            if (!tienePlan && !tieneCheques) {
                const fechaVencGasto = (gasto.fechaVencimientoGasto || gasto.fechaGasto || '').substring(0, 10);
                if (fechaVencGasto) {
                    if (!mapa[fechaVencGasto]) mapa[fechaVencGasto] = [];
                    mapa[fechaVencGasto].push({
                        idUnico: `gasto-${gasto.id}`,
                        tipo: 'COMPROBANTE',
                        gastoOriginal: gasto,
                        labelCelda: `${gasto.proveedorNombre} ${gasto.numeroComprobante ? `(${gasto.numeroComprobante})` : ''}`,
                        monto: parseFloat(gasto.montoTotal || gasto.gastoFinal) || 0,
                        estatus: gasto.estatus,
                        esPagado: gasto.estatus === 'Pagado',
                        esVencido: isGastoVencido(gasto)
                    });
                }
            }

            // 2. CUOTAS
            if (tienePlan) {
                gasto.cuotasPlan.forEach((cuota, index) => {
                    if (!cuota.fechaVencimiento) return;
                    const fechaCuota = cuota.fechaVencimiento.substring(0, 10);
                    if (!mapa[fechaCuota]) mapa[fechaCuota] = [];

                    const esCuotaVencida = !cuota.pagada && fechaCuota < todayKey;

                    mapa[fechaCuota].push({
                        idUnico: `cuota-${gasto.id}-${index}`,
                        tipo: 'CUOTA',
                        cuotaIndex: index,
                        gastoOriginal: gasto,
                        labelCelda: `🏛️️ Cuota ${cuota.numero} - ${gasto.proveedorNombre}`,
                        monto: parseFloat(cuota.monto) || 0,
                        estatus: cuota.pagada ? 'Pagado' : 'Pendiente de Pago',
                        esPagado: !!cuota.pagada,
                        esVencido: esCuotaVencida
                    });
                });
            }

            // 3. CHEQUES
            if (tieneCheques) {
                gasto.chequesList.forEach((chk, index) => {
                    if (!chk.fechaVencimiento) return;
                    const fechaChk = chk.fechaVencimiento.substring(0, 10);
                    if (!mapa[fechaChk]) mapa[fechaChk] = [];

                    const estaPagado = !!chk.pagada;
                    const numChk = chk.numero ? `CHK-${chk.numero}` : 'CHEQUE';

                    mapa[fechaChk].push({
                        idUnico: `cheque-${gasto.id}-${index}`,
                        tipo: 'CHEQUE',
                        chequeIndex: index,
                        gastoOriginal: gasto,
                        labelCelda: `📜 ${numChk} - ${gasto.proveedorNombre}`,
                        monto: parseFloat(chk.monto) || 0,
                        estatus: estaPagado ? 'Pagado' : 'Pendiente de Pago',
                        esPagado: estaPagado,
                        esVencido: !estaPagado && fechaChk < todayKey
                    });
                });
            }
        });

        return { mapaEventosPorFecha: mapa, totalGastosVencidos: vencidosCount };
    }, [gastos, todayKey, isGastoVencido]);

    const calendarDays = useMemo(() => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();

        const firstDayOfMonth = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        const days = [];

        for (let i = 0; i < firstDayOfMonth; i++) {
            days.push({ empty: true, key: `empty-${i}` });
        }

        for (let d = 1; d <= daysInMonth; d++) {
            const mStr = String(month + 1).padStart(2, '0');
            const dStr = String(d).padStart(2, '0');
            const dateKey = `${year}-${mStr}-${dStr}`;

            const itemsDia = mapaEventosPorFecha[dateKey] || [];
            const totalDia = itemsDia.reduce((sum, item) => sum + item.monto, 0);
            
            const tieneVencidos = itemsDia.some(item => item.esVencido);
            const tienePendientes = itemsDia.some(item => !item.esPagado);

            days.push({
                empty: false,
                dayNumber: d,
                dateKey,
                items: itemsDia,
                totalDia,
                tienePendientes,
                tieneVencidos
            });
        }

        return days;
    }, [currentDate, mapaEventosPorFecha]);

    const handlePrevMonth = useCallback(() => {
        setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    }, []);

    const handleNextMonth = useCallback(() => {
        setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    }, []);

    const handleOpenEditGasto = useCallback((gasto, e) => {
        if (e) e.stopPropagation();
        setEditingGasto(gasto);
        setShowModal(true);
    }, []);

    const handleToggleChequePagado = useCallback(async (gastoOriginal, chequeIndex, e) => {
        if (e) e.stopPropagation();

        const nuevosCheques = gastoOriginal.chequesList.map((c, idx) => {
            if (idx === chequeIndex) {
                return { ...c, pagada: !c.pagada };
            }
            return c;
        });

        const todosPagados = nuevosCheques.every(c => c.pagada);
        const nuevoEstatus = todosPagados ? 'Pagado' : 'Parcialmente Pagado';

        try {
            await saveGastoCompra({
                ...gastoOriginal,
                chequesList: nuevosCheques,
                estatus: nuevoEstatus
            }, gastoOriginal.id);
            
            fetchGastos();
        } catch (err) {
            console.error("Error al actualizar cheque:", err);
        }
    }, [fetchGastos]);

    const handleToggleCuotaPagada = useCallback(async (gastoOriginal, cuotaIndex, e) => {
        if (e) e.stopPropagation();

        const nuevasCuotas = gastoOriginal.cuotasPlan.map((c, idx) => {
            if (idx === cuotaIndex) {
                return { ...c, pagada: !c.pagada };
            }
            return c;
        });

        const todasPagadas = nuevasCuotas.every(c => c.pagada);
        const nuevoEstatus = todasPagadas ? 'Pagado' : 'Parcialmente Pagado';

        try {
            await saveGastoCompra({
                ...gastoOriginal,
                cuotasPlan: nuevasCuotas,
                estatus: nuevoEstatus
            }, gastoOriginal.id);
            
            fetchGastos();
        } catch (err) {
            console.error("Error al actualizar cuota:", err);
        }
    }, [fetchGastos]);

    const gastosDiaSeleccionado = useMemo(() => {
        const items = mapaEventosPorFecha[selectedDateKey] || [];
        const gastosUnicosMap = new Map();
        
        items.forEach(ev => {
            if (ev.gastoOriginal && !gastosUnicosMap.has(ev.gastoOriginal.id)) {
                gastosUnicosMap.set(ev.gastoOriginal.id, ev.gastoOriginal);
            }
        });

        return Array.from(gastosUnicosMap.values());
    }, [selectedDateKey, mapaEventosPorFecha]);

    if (loading) return (
        <div className="flex h-full w-full flex-col items-center justify-center bg-white space-y-6 text-left">
            <div className="w-16 h-16 border-4 border-amber-600/20 border-t-amber-600 rounded-full animate-spin"></div>
            <div className="flex flex-col items-center gap-2 text-center text-slate-900">
                <span className="font-black text-[10px] uppercase tracking-[0.3em] text-amber-600 animate-pulse">Sincronizando Agenda de Pagos</span>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-8">Auditando vencimientos de comprobantes, cuotas e impuestos...</span>
            </div>
        </div>
    );

    return (
        <div className="flex h-full w-full flex-col overflow-hidden bg-slate-50 font-['Inter'] antialiased text-left text-slate-900">
            
            {/* BARRA SUPERIOR DE NAVEGACIÓN CON BOTÓN ATRÁS */}
            <div className="flex h-16 shrink-0 items-center justify-between border-b border-slate-200 px-6 bg-white z-[110] select-none">
                <div className="flex items-center gap-4">
                    {/* BOTÓN ATRÁS */}
                    <button 
                        type="button"
                        onClick={handleVolver} 
                        className="group flex items-center gap-2 px-3 py-1.5 text-xs font-bold text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition-all cursor-pointer border border-slate-200"
                        title="Volver"
                    >
                        <IconArrowLeft />
                        <span>Atrás</span>
                    </button>

                    <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-xl">
                        <button
                            onClick={handlePrevMonth}
                            className="w-8 h-8 rounded-lg bg-white hover:bg-slate-200 text-slate-700 flex items-center justify-center font-black transition-all shadow-xs cursor-pointer"
                        >
                            ◄
                        </button>
                        <span className="px-3 text-xs font-black uppercase text-slate-900 tracking-wider">
                            {MESES[currentDate.getMonth()]} {currentDate.getFullYear()}
                        </span>
                        <button
                            onClick={handleNextMonth}
                            className="w-8 h-8 rounded-lg bg-white hover:bg-slate-200 text-slate-700 flex items-center justify-center font-black transition-all shadow-xs cursor-pointer"
                        >
                            ►
                        </button>
                    </div>

                    <button
                        onClick={() => {
                            setCurrentDate(new Date());
                            setSelectedDateKey(todayKey);
                        }}
                        className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-bold uppercase text-[10px] tracking-wider transition-all cursor-pointer"
                    >
                        Hoy
                    </button>

                    {totalGastosVencidos > 0 && (
                        <div className="flex items-center gap-2 bg-rose-500 text-white px-3 py-1 rounded-xl text-xs font-black animate-pulse shadow-sm">
                            <span>⚠️ {totalGastosVencidos} {totalGastosVencidos === 1 ? 'Vencimiento Pendiente' : 'Vencimientos Pendientes'}</span>
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-4 text-xs font-bold">
                    <div className="flex items-center gap-1.5">
                        <span className="w-3 h-3 rounded-full bg-rose-600 inline-block animate-pulse"></span>
                        <span className="text-rose-700 font-extrabold">Vencidas Impagas</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <span className="w-3 h-3 rounded-full bg-amber-500 inline-block"></span>
                        <span className="text-slate-600">Pendientes</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <span className="w-3 h-3 rounded-full bg-emerald-500 inline-block"></span>
                        <span className="text-slate-600">Pagados</span>
                    </div>
                </div>
            </div>

            {/* GRILLA Y PANEL LATERAL */}
            <div className="flex-grow flex flex-col lg:flex-row overflow-hidden w-full">
                
                {/* GRILLA DEL CALENDARIO */}
                <main className="flex-grow p-4 md:p-6 overflow-y-auto custom-scrollbar">
                    <div className="bg-white rounded-3xl border border-slate-200 shadow-xs p-4 space-y-2">
                        
                        <div className="grid grid-cols-7 gap-2 select-none text-center border-b border-slate-100 pb-2">
                            {DIAS_SEMANA.map((dia, idx) => (
                                <div key={dia} className={`text-[10px] font-black uppercase tracking-wider ${idx === 0 || idx === 6 ? 'text-rose-400' : 'text-slate-400'}`}>
                                    {dia}
                                </div>
                            ))}
                        </div>

                        <div className="grid grid-cols-7 gap-2">
                            {calendarDays.map((d) => {
                                if (d.empty) {
                                    return <div key={d.key} className="h-28 rounded-2xl bg-slate-50/50 border border-transparent"></div>;
                                }

                                const isSelected = d.dateKey === selectedDateKey;

                                return (
                                    <div
                                        key={d.dateKey}
                                        onClick={() => setSelectedDateKey(d.dateKey)}
                                        className={`h-28 rounded-2xl p-2 border transition-all cursor-pointer flex flex-col justify-between relative ${
                                            isSelected 
                                                ? 'bg-white border-indigo-500 ring-2 ring-indigo-500 shadow-md' 
                                                : d.tieneVencidos
                                                    ? 'bg-rose-50/50 border-rose-200 hover:border-rose-400'
                                                    : d.items.length > 0
                                                        ? 'bg-white border-slate-200 hover:border-indigo-300 hover:shadow-sm'
                                                        : 'bg-white border-slate-100 hover:bg-slate-50'
                                        }`}
                                    >
                                        <div className="flex justify-between items-center shrink-0">
                                            <span className={`text-xs font-black font-mono px-2 py-0.5 rounded-lg ${
                                                isSelected 
                                                    ? 'bg-indigo-600 text-white' 
                                                    : d.tieneVencidos
                                                        ? 'bg-rose-600 text-white'
                                                        : 'bg-slate-100 text-slate-800'
                                            }`}>
                                                {d.dayNumber}
                                            </span>

                                            {d.items.length > 0 && (
                                                <span className={`w-2.5 h-2.5 rounded-full ${
                                                    d.tieneVencidos 
                                                        ? 'bg-rose-600 animate-ping' 
                                                        : d.tienePendientes 
                                                            ? 'bg-amber-500' 
                                                            : 'bg-emerald-500'
                                                }`}></span>
                                            )}
                                        </div>

                                        <div className="space-y-1 overflow-y-auto max-h-[55px] scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent flex-grow pt-1 my-0.5 pr-0.5">
                                            {d.items.map((ev) => (
                                                <div
                                                    key={ev.idUnico}
                                                    onClick={(e) => handleOpenEditGasto(ev.gastoOriginal, e)}
                                                    className={`px-1.5 py-0.5 rounded text-[9px] font-bold truncate uppercase transition-transform hover:scale-102 flex items-center justify-between cursor-pointer ${
                                                        ev.esVencido
                                                            ? 'bg-rose-600 text-white font-black'
                                                            : ev.esPagado
                                                                ? 'bg-emerald-50 text-emerald-800 border border-emerald-100'
                                                                : 'bg-amber-50 text-amber-900 border border-amber-200'
                                                    }`}
                                                    title={`${ev.labelCelda} - ${formatCurrency(ev.monto)}`}
                                                >
                                                    <span className="truncate">{ev.labelCelda}</span>
                                                    {ev.esVencido && <span className="ml-1 shrink-0">⚠️</span>}
                                                </div>
                                            ))}
                                        </div>

                                        {d.totalDia > 0 && (
                                            <div className="text-[9px] font-mono font-black text-right border-t border-slate-100 pt-1 text-slate-900 shrink-0">
                                                {formatCurrency(d.totalDia)}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                    </div>
                </main>

                {/* AGENDA LATERAL */}
                <aside className="w-full lg:w-96 border-t lg:border-t-0 lg:border-l border-slate-200 bg-white p-6 shrink-0 flex flex-col overflow-hidden">
                    <div className="border-b border-slate-100 pb-4 mb-4 select-none">
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
                            Agenda de Vencimientos
                        </span>
                        <h3 className="text-base font-black text-slate-900 uppercase">
                            {formatDate(selectedDateKey)}
                        </h3>
                    </div>

                    <div className="flex-grow overflow-y-auto space-y-4 custom-scrollbar pr-1">
                        {gastosDiaSeleccionado.length === 0 ? (
                            <div className="text-center py-12 space-y-2">
                                <span className="text-2xl block">🎉</span>
                                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                                    Sin vencimientos para esta fecha
                                </p>
                            </div>
                        ) : (
                            gastosDiaSeleccionado.map(gasto => {
                                const chequesDelDia = Array.isArray(gasto.chequesList)
                                    ? gasto.chequesList
                                        .map((chk, idx) => ({ ...chk, indexOriginal: idx }))
                                        .filter(chk => (chk.fechaVencimiento || '').substring(0, 10) === selectedDateKey)
                                    : [];

                                const cuotasDelDia = gasto.esPlanPago && Array.isArray(gasto.cuotasPlan)
                                    ? gasto.cuotasPlan
                                        .map((c, idx) => ({ ...c, indexOriginal: idx }))
                                        .filter(c => (c.fechaVencimiento || '').substring(0, 10) === selectedDateKey)
                                    : [];

                                const tieneChequesHoy = chequesDelDia.length > 0;
                                const tieneCuotasHoy = cuotasDelDia.length > 0;
                                const vencido = isGastoVencido(gasto);

                                return (
                                    <div
                                        key={gasto.id}
                                        onClick={() => handleOpenEditGasto(gasto)}
                                        className={`p-4 rounded-2xl border transition-all cursor-pointer space-y-3 group ${
                                            vencido
                                                ? 'bg-rose-50/80 border-rose-300 shadow-sm hover:border-rose-500'
                                                : 'bg-slate-50 border-slate-200/90 hover:bg-white hover:border-slate-300 hover:shadow-md'
                                        }`}
                                    >
                                        <div className="flex justify-between items-start gap-2">
                                            <div>
                                                <h4 className="text-xs font-black text-slate-900 uppercase group-hover:text-indigo-700 transition-colors">
                                                    {gasto.proveedorNombre}
                                                </h4>
                                                
                                                <div className="flex items-center gap-1.5 mt-0.5">
                                                    <span className="text-[10px] font-extrabold text-indigo-900 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded-md uppercase">
                                                        {gasto.tipoComprobante || 'Comprobante'}: {gasto.numeroComprobante || 'S/N'}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="flex flex-col items-end gap-1 shrink-0">
                                                {vencido ? (
                                                    <span className="px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase bg-rose-600 text-white shadow-sm animate-pulse flex items-center gap-1">
                                                        ⚠️ VENCIDA
                                                    </span>
                                                ) : (
                                                    <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase border ${getStatusBadgeStyle(gasto.estatus)}`}>
                                                        {gasto.estatus}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex justify-between items-center text-xs pt-1 border-t border-slate-200/60">
                                            <span className="text-[10px] font-bold text-slate-500 uppercase">
                                                {gasto.conceptoNombre || 'Sin concepto'}
                                            </span>
                                            <span className="font-mono font-black text-rose-700 text-sm">
                                                {formatCurrency(gasto.montoTotal || gasto.gastoFinal)}
                                            </span>
                                        </div>

                                        {/* CUOTAS DEL DÍA */}
                                        {tieneCuotasHoy && (
                                            <div className="bg-indigo-50/60 border border-indigo-200/80 rounded-xl p-2.5 space-y-2 text-xs">
                                                <span className="text-[9px] font-black text-indigo-900 uppercase tracking-wider block">
                                                    🏛️ Plan de Pagos - Cuotas ({cuotasDelDia.length})
                                                </span>

                                                <div className="space-y-1.5">
                                                    {cuotasDelDia.map((c) => (
                                                        <div key={c.indexOriginal} className="bg-white p-2 rounded-lg border border-indigo-200/60 flex items-center justify-between text-[11px]">
                                                            <div>
                                                                <span className="font-bold text-indigo-950 block">Cuota N° {c.numero}</span>
                                                                <span className="text-[9px] font-bold text-slate-400">Vence Hoy: {formatDate(c.fechaVencimiento)}</span>
                                                            </div>

                                                            <div className="flex items-center gap-2">
                                                                <span className="font-mono font-black text-indigo-900">{formatCurrency(c.monto)}</span>
                                                                
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => handleToggleCuotaPagada(gasto, c.indexOriginal, e)}
                                                                    className={`px-2.5 py-1 rounded-md text-[9px] font-black uppercase transition-all cursor-pointer ${
                                                                        c.pagada
                                                                            ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                                                                            : 'bg-amber-100 text-amber-800 border border-amber-300'
                                                                    }`}
                                                                >
                                                                    {c.pagada ? '✓ Pagado' : '⏳ Pendiente'}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* CHEQUES DEL DÍA */}
                                        {tieneChequesHoy && (
                                            <div className="bg-amber-50/60 border border-amber-200/80 rounded-xl p-2.5 space-y-2 text-xs">
                                                <span className="text-[9px] font-black text-amber-900 uppercase tracking-wider block">
                                                    📜 Cheques del Día ({chequesDelDia.length})
                                                </span>

                                                <div className="space-y-1.5">
                                                    {chequesDelDia.map((chk) => {
                                                        const estaPagado = !!chk.pagada;
                                                        return (
                                                            <div key={chk.indexOriginal} className="bg-white p-2 rounded-lg border border-amber-200/60 flex items-center justify-between text-[11px]">
                                                                <div>
                                                                    <span className="font-mono font-bold text-slate-900 block">CHK-{chk.numero} ({chk.cuentaNombre || chk.banco || 'S/D'})</span>
                                                                    <span className="text-[9px] font-bold text-slate-400">Vence Hoy: {formatDate(chk.fechaVencimiento)}</span>
                                                                </div>

                                                                <div className="flex items-center gap-2">
                                                                    <span className="font-mono font-black text-slate-800">{formatCurrency(chk.monto)}</span>
                                                                    
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => handleToggleChequePagado(gasto, chk.indexOriginal, e)}
                                                                        className={`px-2.5 py-1 rounded-md text-[9px] font-black uppercase transition-all cursor-pointer ${
                                                                            estaPagado
                                                                                ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                                                                                : 'bg-amber-100 text-amber-800 border border-amber-300'
                                                                        }`}
                                                                    >
                                                                        {estaPagado ? '✓ Pagado' : '⏳ Pendiente'}
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        <button
                                            type="button"
                                            onClick={(e) => handleOpenEditGasto(gasto, e)}
                                            className={`w-full py-1.5 text-white rounded-xl font-bold uppercase text-[10px] transition-all cursor-pointer ${
                                                vencido ? 'bg-rose-600 hover:bg-rose-700' : 'bg-slate-900 hover:bg-slate-800'
                                            }`}
                                        >
                                            ⚙️ Abrir / Editar Comprobante
                                        </button>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </aside>

            </div>

            {/* MODAL CONECTADO */}
            <ModalGastoCompra
                show={showModal}
                onClose={() => setShowModal(false)}
                onSuccess={fetchGastos}
                initialData={editingGasto}
                maestros={maestros}
            />
        </div>
    );
};

export default memo(CalendarioPagosTab);